import { camelCase } from '@s-libs/micro-dash';
import ActionTracker, { SegmentTracker } from 'llt-action-tracker';
import { TrackEventArgsType, TrackEventNameType } from '../@types/schema';
import transformParams from './transform-params';

interface Tracker {
  track(params: any): Promise<void>;
}

const defaultTracker: Tracker = new ActionTracker({
  applicationId: process.env.NEXT_PUBLIC_ACTIONTRACKER_APPID,
  endpoint: process.env.NEXT_PUBLIC_ACTIONTRACKER_ENDPOINT,
});

let segmentTrackerInstances: Record<string, Tracker> = {};

const initInstance = (segmentWriteKey: string, params?: Record<string, unknown>): Tracker => {
  const currentInstance = segmentTrackerInstances[segmentWriteKey];
  if (currentInstance) return currentInstance;
  const newInstance = SegmentTracker({
    ...params,
    segmentWriteKey,
  });
  segmentTrackerInstances[segmentWriteKey] = newInstance;
  return newInstance;
};

const defaultSegmentTracker: Tracker = initInstance(
  process.env.NEXT_PUBLIC_SEGMENT_LLT_ACTIONTRACKER_ID,
  { excludeEvents: ['apiFetch'] }
);

const getTrackingProperty = (el, propertyName) => {
  const matched = el.className.match(new RegExp(`js-${propertyName}:([^ ]+)`));
  return el.dataset[camelCase(propertyName)] || (matched && matched[1]);
};

/**
 * Helper function to raise a tracking event on the given DOM element.
 * @param el - The element to track
 * @param eventName - The name of the event
 * @param args - Any custom arguments to track
 */
export const raiseTrackingEvent = (el: Element, eventName: string, args?: any): CustomEvent => {
  const target = el.tagName;
  const className = el.className;
  const href = el.getAttribute('href');
  // TODO: const leadFormId = leadFormSelectors.getLeadFormId(this);
  // TODO: const panelId = leadFormSelectors.getLeadFormPanelId(this) || stepModel.getStepId(this);
  const text = (el.textContent || '').replace(/\s+/, ' ').trim();
  const category = getTrackingProperty(el, 'category') || 'nerdy-marketing';

  const eventArgs = {
    bubbles: true,
    detail: {
      category,
      className,
      eventName,
      href,
      target,
      text,
      ...(args || {}),
    },
  };

  const event = new CustomEvent('track', eventArgs);
  el.dispatchEvent(event);

  return event;
};

/**
 * Tracks an event on the given tracker with the given args, performing transformation and adding
 * any extra necessary args based on the current document. It is exported only for testing purporse
 */
export const trackEvent = async (
  eventName: TrackEventNameType,
  args: TrackEventArgsType,
  tracker: Tracker,
  trackerType?: string
) => {
  const { newrelicMetadata, ...restOfArgs } = args;
  const transformed = transformParams(eventName, restOfArgs);

  if (window['newrelic'])
    window['newrelic'].addPageAction(eventName, {
      ...transformed,
      customEventData: {
        ...transformed?.customEventData,
        ...newrelicMetadata,
        trackerType: trackerType || '',
      },
    });

  // this try catch exists because in localhost this will throw cors errors
  try {
    await tracker.track(transformed);
  } catch (ex) {
    if (ex instanceof TypeError && process.env.NODE_ENV !== 'production') {
      return console.debug('vt-events:', eventName, args);
    }
    return Promise.reject(ex);
  }
};

export function defaultParams(eventName: string, args: Record<string, unknown>) {
  const params = {
    customEventData: {
      action: eventName,
    },
    ...args,
  };
  return params;
}

export function flatTrack(eventName: string, args: Record<string, unknown>, key: string) {
  if (key) {
    const params = defaultParams(eventName, args);
    return initInstance(key).track(params);
  }
}

/**
 * Tracks an event using action tracker as default trackers
 */
export const track = async (
  eventName: TrackEventNameType,
  args: TrackEventArgsType,
  actionTracker = defaultTracker,
  segmentTracker = defaultSegmentTracker
) => {
  const promises = [
    trackEvent(eventName, args, segmentTracker, 'segmentTracker'),
    trackEvent(eventName, args, actionTracker, 'actionTracker'),
  ];

  return Promise.all(promises);
};
